import { type ComponentPropsWithoutRef, type ElementType } from "react";
import { type NetPriceData, type PriceData, type TotalPriceData, type WithSurcharge } from "~/models/PriceData";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { obtainNetPriceLabels, obtainTotalPriceLabels } from "~/common/components/Price/v2/NetPrice";
import { formatCurrency } from "~/utils/currencyFormatter";

export type PriceProps<T extends ElementType = "span"> = {
  as?: T;
  price: PriceData | (PriceData & WithSurcharge) | undefined | null;
  surchargeEnabled?: boolean;
  tariffSurcharge?: boolean;
  zeroText?: string;
} & ComponentPropsWithoutRef<T>;

export function Price<T extends ElementType = "span">({
  as,
  price,
  surchargeEnabled,
  tariffSurcharge = false,
  zeroText = "-",
  ...rest
}: PriceProps<T>) {
  const Tag = as ?? "span";
  let text;
  if (!price || !price.value) {
    text = zeroText;
  } else {
    const hasSurcharge = surchargeEnabled && "surcharge" in price && !!price.surcharge;

    let val: number;
    if (hasSurcharge) {
      val = price.value;
    } else {
      val = "baseValue" in price ? price.baseValue : price.value;
    }
    text = formatCurrency(val, price.currencyIso);

    if (hasSurcharge || tariffSurcharge) {
      text += "*";
    }
  }
  return (
    <Tag data-test-id={"value"} {...rest}>
      {text}
    </Tag>
  );
}

type NetPriceUnitProps<T extends ElementType> = {
  as?: T;
  price: NetPriceData | undefined | null;
  variant?: "full" | "short";
} & ComponentPropsWithoutRef<T> &
  Parameters<typeof obtainNetPriceLabels>[2];

Price.NetPriceUnit = <T extends ElementType = "span">({
  as,
  price,
  surchargeEnabled,
  className,
  zeroText,
  variant = "full",
  ...rest
}: NetPriceUnitProps<T>) => {
  const { t } = useTranslation();
  const labels = obtainNetPriceLabels(price, t, { surchargeEnabled, zeroText });

  const Tag = as ?? "span";
  return (
    <Tag data-test-id={"unit"} className={classNames("f-label-color", className)} {...rest}>
      {variant === "full" ? labels.fullLabel : labels.unit}
    </Tag>
  );
};

type ExtraLabelProps<T extends ElementType> = {
  as?: T;
  price: NetPriceData | undefined | null;
  variant?: "full" | "short";
} & ComponentPropsWithoutRef<T> &
  Parameters<typeof obtainNetPriceLabels>[2];

Price.ExtraLabels = <T extends ElementType = "div">({
  as,
  price,
  surchargeEnabled,
  zeroText,
  variant = "full",
  ...rest
}: ExtraLabelProps<T>) => {
  const { t } = useTranslation();
  const labels = obtainNetPriceLabels(price, t, { surchargeEnabled, zeroText });

  const Tag = as ?? "div";
  return (
    <Tag {...rest}>
      {labels.extraLabels.map((label, i) => (
        <div key={i}>{label[variant]}</div>
      ))}
    </Tag>
  );
};

type TotalPriceUnitProps<T extends ElementType> = {
  as?: T;
  price: TotalPriceData | undefined | null;
  variant?: "full" | "short";
} & ComponentPropsWithoutRef<T> &
  Parameters<typeof obtainTotalPriceLabels>[2];

Price.TotalPriceUnit = <T extends ElementType = "span">({
  as,
  price,
  surchargeEnabled,
  className,
  zeroText,
  variant = "full",
  ...rest
}: TotalPriceUnitProps<T>) => {
  const { t } = useTranslation();
  const labels = obtainTotalPriceLabels(price, t, { surchargeEnabled, zeroText });

  const Tag = as ?? "span";
  return (
    <Tag data-test-id={"unit"} className={classNames("f-label-color", className)} {...rest}>
      {variant === "full" ? labels.fullLabel : labels.unit}
    </Tag>
  );
};
